import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useT } from '@/app/i18n/client';
import { ACCOMMODATION_QUERY_KEYS } from '@/entities/accommodation/keys';
import type { IAccommodationOptionType, ISelectedDates } from '@/entities/accommodation/types';
import type { BookedRoom } from '@/entities/room/types';
import apiGetAccommodationRoom from '@/features/accommodation/apis/get_room';
import apiGetAccommodationRoomPriceList from '@/features/accommodation/apis/get_room_price_list';
import apiGetAccommodationRoomStatusList from '@/features/accommodation/apis/get_room_status_list';
import { utilDateGetMonth, utilDateThisMonth } from '@/lib/date';
import { useProductStore } from '@/stores/product';

import { useShowCalendarContext } from '../contexts/ShowCalendarStoreContext';

export const useAccommodationState = (idx: string, isExpanded: boolean) => {
  const { t, i18n } = useT('product');

  const setBookedRooms = useProductStore(state => state.setBookedRooms);
  const { showCalendarButtonRef, showCalendar, toggleCalendar } = useShowCalendarContext();

  const [addPeopleQuantity, setAddPeopleQuantity] = useState<number>(0);

  const [selectedOption, setSelectedOption] = useState<IAccommodationOptionType | null>(null);

  // Calendar states
  const [currentMonth, setCurrentMonth] = useState<Date>(utilDateThisMonth());
  const [selectedDates, setSelectedDates] = useState<ISelectedDates>({
    checkIn: null,
    checkOut: null,
  });

  const { data: roomData } = useQuery({
    queryKey: [ACCOMMODATION_QUERY_KEYS.GET_ROOM, idx, i18n.language],
    queryFn: () => apiGetAccommodationRoom({ idx, lng: i18n.language }),
    enabled: isExpanded,
    throwOnError: true,
    retry: false,
  });

  const { data: roomStatusList = [] } = useQuery({
    queryKey: [ACCOMMODATION_QUERY_KEYS.GET_ROOM_STATUS_LIST, idx],
    queryFn: async () => {
      const roomStatusList = await apiGetAccommodationRoomStatusList({ idx });

      // roomStatusList에서 isActive=true 이면서 r.date가 오늘(이상)인 첫 요소의 date를 월 첫 날로 설정
      const todayMonth = utilDateThisMonth();
      const firstActiveDate = roomStatusList.find(
        r => r.isActive && new Date(r.date) >= todayMonth,
      )?.date;
      if (firstActiveDate) {
        setCurrentMonth(utilDateGetMonth(firstActiveDate));
      }

      return roomStatusList;
    },
    enabled: isExpanded,
    throwOnError: true,
    retry: false,
  });

  const { data: roomPriceList = [] } = useQuery({
    queryKey: [
      ACCOMMODATION_QUERY_KEYS.GET_ROOM_PRICE_LIST,
      idx,
      selectedDates.checkIn,
      selectedDates.checkOut,
    ],
    queryFn: () =>
      apiGetAccommodationRoomPriceList({
        idx,
        startDate: selectedDates.checkIn,
        endDate: selectedDates.checkOut,
      }),
    enabled: isExpanded,
    throwOnError: true,
    retry: false,
  });

  // 날짜 범위 생성 헬퍼 함수
  const getDateRange = useCallback((start: Date, end: Date): Date[] => {
    const dates: Date[] = [];
    const current = new Date(start);
    while (current < end) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return dates;
  }, []);

  // 선택한 날짜 기간 동안 roomPriceList addPeoplePrice 가져오기
  const selectedPeriodAddPeoplePrice = useMemo((): number => {
    if (!selectedDates.checkIn || !selectedDates.checkOut) {
      return 0;
    }

    // 날짜 문자열 포맷 함수
    const getDateStr = (date: Date) => {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };

    // 선택한 날짜 기간 내의 roomPriceList 항목들의 추가 인원 가격 합계
    const checkIn = new Date(selectedDates.checkIn);
    const checkOut = new Date(selectedDates.checkOut);
    const dateRange = getDateRange(checkIn, checkOut);

    return dateRange.reduce((total, date) => {
      const dateStr = getDateStr(date);
      const priceItem = roomPriceList.find(item => item.date === dateStr);
      return total + (priceItem?.addPeoplePrice || 0);
    }, 0);
  }, [selectedDates.checkIn, selectedDates.checkOut, getDateRange, roomPriceList]);

  // 선택한 날짜 기간 동안의 roomPriceList 필터링
  const selectedPeriodRoomPriceList = useMemo(() => {
    if (!selectedDates.checkIn || !selectedDates.checkOut) {
      return [];
    }

    // 날짜 문자열 포맷 함수
    const getDateStr = (date: Date) => {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };

    // 선택한 날짜 기간 내의 roomPriceList 항목들 필터링
    const checkIn = new Date(selectedDates.checkIn);
    const checkOut = new Date(selectedDates.checkOut);
    const dateRange = getDateRange(checkIn, checkOut);

    return dateRange
      .map(date => {
        const dateStr = getDateStr(date);
        return roomPriceList.find(item => item.date === dateStr);
      })
      .filter((item): item is NonNullable<typeof item> => item !== undefined);
  }, [selectedDates.checkIn, selectedDates.checkOut, getDateRange, roomPriceList]);

  const handleChangeMonth =
    (newMonth: Date) =>
    (e: React.MouseEvent<HTMLButtonElement>): void => {
      e.stopPropagation();

      setCurrentMonth(newMonth);
    };

  // Pricing constants

  const updateQuantity =
    (change: number) =>
    (e: React.MouseEvent<HTMLButtonElement>): void => {
      e.stopPropagation();

      setAddPeopleQuantity(prev => prev + change);
    };

  const handleOptionSelection =
    (option: IAccommodationOptionType) => (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setSelectedOption(prev => (prev?.idx === option.idx ? null : option));
    };

  const addRoom = (): void => {
    if (!selectedDates.checkIn || !selectedDates.checkOut) {
      toast.error(t('messages.selectDate'));
      if (!showCalendar) toggleCalendar();
      showCalendarButtonRef?.current?.focus();
      return;
    }

    const newRoom: BookedRoom = {
      idx,
      name: roomData?.title || '',
      standardPeople: roomData?.standardPeople || 0,
      addPeopleQuantity: addPeopleQuantity,
      addPeoplePrice: selectedPeriodAddPeoplePrice,
      option: selectedOption,
      priceList: roomPriceList,
      checkIn: selectedDates.checkIn,
      checkOut: selectedDates.checkOut,
      checkInTime: roomData?.checkInTime || '',
      checkOutTime: roomData?.checkOutTime || '',
      totalRoomPrice: totalRoomPrice,
      totalExtraGuestPrice: totalExtraGuestPrice,
      totalOptionPrice: totalOptionPrice,
      totalAmount: totalRoomPrice + totalExtraGuestPrice + totalOptionPrice,
    };
    // zustand store에 저장
    setBookedRooms(prev => [...prev, newRoom]);

    // 초기화
    setSelectedDates({
      checkIn: null,
      checkOut: null,
    });
    setSelectedOption(null);
    setAddPeopleQuantity(0);

    toast.success(t('success.add', { title: roomData?.title || t('accommodation') }));
  };

  const handleDateClick = (selectedDate: Date) => {
    const clickedDate = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
    );

    // 날짜 문자열 포맷 함수
    const getDateStr = (date: Date) => {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };
    // roomPriceList에서 해당 날짜의 isActive 확인
    const getRoomPriceByDate = (date: Date) => {
      const dateStr = getDateStr(date);
      return roomStatusList.find(r => {
        return r.date === dateStr;
      });
    };
    // 숙박일(체크인 포함 ~ 체크아웃 전날) 내 모든 날짜가 isActive=true인지 확인
    const isRangeActive = (start: Date, end: Date) => {
      const dateRange = getDateRange(start, end);
      return dateRange.every(date => {
        const price = getRoomPriceByDate(date);
        return price?.isActive === true;
      });
    };

    // checkin 날짜를 두 번 누르면 초기화
    if (
      selectedDates.checkIn &&
      !selectedDates.checkOut &&
      clickedDate.getTime() === selectedDates.checkIn.getTime()
    ) {
      setSelectedDates({ checkIn: null, checkOut: null });
      setAddPeopleQuantity(0);
      return;
    }

    if (!selectedDates.checkIn || (selectedDates.checkIn && selectedDates.checkOut)) {
      setSelectedDates({ checkIn: clickedDate, checkOut: null });
      setAddPeopleQuantity(0);
    } else if (selectedDates.checkIn && !selectedDates.checkOut) {
      if (clickedDate > selectedDates.checkIn) {
        if (!isRangeActive(selectedDates.checkIn, clickedDate)) {
          // 중간에 비활성 날짜가 있으면 선택 불가
          return;
        }
        setSelectedDates({ ...selectedDates, checkOut: clickedDate });
      } else {
        setSelectedDates({ checkIn: clickedDate, checkOut: null });
      }
    }
  };

  const totalRoomPrice = useMemo((): number => {
    return selectedPeriodRoomPriceList.reduce((sum, priceItem) => sum + priceItem.price, 0);
  }, [selectedPeriodRoomPriceList]);

  // selectedPeriodRoomPriceList를 사용하여 선택한 날짜의 addPeoplePrice 합계 계산
  const calculatedAddPeoplePrice = selectedPeriodRoomPriceList.reduce(
    (sum, priceItem) => sum + priceItem.addPeoplePrice,
    0,
  );

  // 추가 인원 수에 따른 총 가격 계산
  const totalExtraGuestPrice = calculatedAddPeoplePrice * addPeopleQuantity;

  // 옵션별 가격 계산
  const totalOptionPrice = useMemo((): number => {
    if (!selectedOption) return 0;

    if (selectedOption.type === 'PER_NIGHT') {
      return selectedOption.price * selectedPeriodRoomPriceList.length;
    }

    if (selectedOption.type === 'PER_PERSON') {
      return selectedOption.price * ((roomData?.standardPeople || 0) + addPeopleQuantity);
    }

    if (selectedOption.type === 'PER_ROOM') {
      return selectedOption.price;
    }

    return 0;
  }, [
    addPeopleQuantity,
    roomData?.standardPeople,
    selectedOption,
    selectedPeriodRoomPriceList.length,
  ]);

  return {
    t,
    roomData,
    roomStatusList, // roomStatusList 반환
    // State
    addPeopleQuantity,
    selectedOption,
    currentMonth,
    setCurrentMonth,
    selectedDates,
    setSelectedDates,
    selectedPeriodAddPeoplePrice,
    selectedPeriodRoomPriceList,

    // price
    calculatedAddPeoplePrice,
    totalRoomPrice,
    totalExtraGuestPrice,
    totalOptionPrice,
    totalAmount: totalRoomPrice + totalExtraGuestPrice + totalOptionPrice,

    // Functions
    updateQuantity,
    handleOptionSelection,
    addRoom,
    handleDateClick,
    handleChangeMonth, // 추가
  };
};
